import { useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import Grid from '@mui/material/Grid';

import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpFilledIcon from '@mui/icons-material/ThumbUp'; // Import filled icon

import { useUser } from '../contexts/UserContext'; // Import useUser
import { useEvents } from '../contexts/EventContext'; // Import useEvents
import { useTenant } from '../contexts/TenantContext'; // Added import
import { useTranslation } from 'react-i18next'; // Import useTranslation
import Badge from '@mui/material/Badge'; // Import Badge
import { useTheme } from '@mui/material/styles'; // Import useTheme
import Button from '@mui/material/Button';
import './EventCard.css'; // Import CSS file for AddToCalendarButton centering
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { submitEventComment, submitUserStateAnswer } from '../services/businessService';

const EventCardV3 = ({ event, handleEventClick, formatDate }) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const { t, i18n } = useTranslation(); // Call useTranslation hook and get i18n instance
  const { userData, userProfile } = useUser();
  const { updateSingleEventStatus, fetchUserEventsV2, updateEventRating } = useEvents();
  const { selectedTenantId, tenantData } = useTenant(); // Added import
  const theme = useTheme(); // Initialize theme

  const [isInterested, setIsInterested] = useState(false);
  const [isParticipating, setIsParticipating] = useState(false);

  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (event?.event_user_state) {
      setIsInterested(event.event_user_state.is_interested);
      setIsParticipating(event.event_user_state.is_participating);
    }
  }, [event]);

  const handleStatusChange = async (eventToUpdate, newStatus) => {
    setIsLoading(true);
    if (!eventToUpdate) {
      console.error('No event found for status update');
      return;
    }
    if (!userData || !userData.email) {
      console.error('User data is not available for status update. Please try again.');
      return;
    }
    if (!selectedTenantId) {
      console.error('Tenant ID is not available for status update.');
      return;
    }

    let newIsInterested = isInterested;
    let newIsParticipating = isParticipating;

    // Update local state immediately for responsiveness
    if (newStatus === 'interested') {
      if (isInterested) {
        // If already interested, clicking again removes interest
        newIsInterested = false;
        setIsInterested(false);
      } else {
        newIsInterested = true;
        setIsInterested(true);
        newIsParticipating = false;
        setIsParticipating(false);
      }
    } else if (newStatus === 'participate') {
      if (isParticipating) {
        // If already participating, clicking again removes participation
        newIsParticipating = false;
        setIsParticipating(false);
      } else {
        newIsInterested = false;
        setIsInterested(false);
        newIsParticipating = true;
        setIsParticipating(true);
      }
    }

    const statusToSend = { "isInterested": newIsInterested, "isParticipating": newIsParticipating };

    try {
      await submitUserStateAnswer(selectedTenantId, eventToUpdate.event_id, statusToSend);
      updateSingleEventStatus(eventToUpdate.event_id, statusToSend);
      fetchUserEventsV2().then(() => {
        setIsLoading(false);
        console.log('User events refreshed after status update');
      }).catch((error) => {
        setIsLoading(false);
        console.error('Error refreshing user events after status update:', error);
      });

    } catch (error) {
      console.error('Error submitting user state answer:', error);
    } finally {
      
    }
  };

  // Adjusted labels for precision={1} using t function
  const labels = {
    1: t('eventCard.rating.useless'),
    2: t('eventCard.rating.poor'),
    3: t('eventCard.rating.ok'),
    4: t('eventCard.rating.good'),
    5: t('eventCard.rating.excellent'),
  };

  function getLabelText(value) {
    // Ensure value is a key in labels, otherwise, don't append undefined
    const label = labels[value];
    return `${value} ${t('eventCard.rating.banana', { count: value })}${label ? `, ${label}` : ''}`;
  }

  const getCalendarDate = (isoString) => {
    if (!isoString) return undefined;
    return isoString.substring(0, 10); // YYYY-MM-DD
  };

  const getCalendarTime = (isoString) => {
    if (!isoString) return '';
    return isoString.split('T')[1].substring(0, 5); // HH:MM
  };

  // Helper function to format duration
  const formatDuration = (minutes) => {
    if (!minutes) return t('eventCard.unknown');

    // For durations >= 24 hours, show in days
    if (minutes >= 1440) { // 24 hours * 60 minutes
      const days = Math.floor(minutes / 1440);
      return t('eventCard.durationDays', { count: days, days });
    }

    // For durations < 24 hours, show in hours and minutes
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      // Less than an hour, show just minutes
      return t('eventCard.durationMinutes', { count: minutes, duration: minutes });
    } else {
      // Format as hours and minutes (e.g., "1h30")
      return t('eventCard.durationHours', {
        count: hours,
        hours,
        minutes: remainingMinutes > 0 ? remainingMinutes : ''
      });
    }
  };

  if (!event) return null;

  const eventStartDate = new Date(event.start_time);
  const now = new Date();
  const isEventInThePast = eventStartDate < now;

  const calendarProps = {
    name: event.title,
    description: event.description || '',
    options: ['Google', 'Microsoft365'],
    location: event.location || '',
    timeZone: 'Europe/Paris',
    startDate: getCalendarDate(event.start_time),
    startTime: getCalendarTime(event.start_time),
    listStyle: "overlay",
    // buttonStyle: "text",
    language: i18n.language === 'fr' ? 'fr' : 'en', // Set language for AddToCalendarButton
    // styleLight: "--btn-background:rgb(229, 255, 0); --btn-background-image: linear-gradient(120deg, #fff000 0, #f6fb00 100%); --btn-text: rgb(0, 0, 0); --font: Georgia, 'Times New Roman', Times, serif;",
    // styleDark: "--btn-background: rgb(229, 255, 0);",
  };

  if (userProfile && userProfile.auth_provider) {
    if (userProfile.auth_provider === 'google') {
      calendarProps.options = ['Google'];
    } else if (userProfile.auth_provider === 'azure') {
      calendarProps.options = ['Microsoft365'];
    }
  }

  

  if (event.end_time) {
    calendarProps.endDate = getCalendarDate(event.end_time);
    calendarProps.endTime = getCalendarTime(event.end_time);
  } else if (event.duration_minutes) {
    calendarProps.duration = event.duration_minutes;
  }

  // console.log('Calendar Props:', calendarProps);
  // If neither end_dt nor duration_minutes is provided,
  // the library will likely use a default duration (e.g., 1 hour).
  // Make the whole card clickable except when interacting with the rating
  const handleCardClick = (e) => {

    navigate('/app/' + tenantData.subdomain_prefix + '/events/' + event.event_id);
    // if (!isRatingActive) {
    //   handleEventClick(event.id);
    // }
  };


  return (
    <Card
      className="event-card"
      sx={(theme) => ({
        minWidth: isMobile ? '350px' : '300px',
        width: isMobile ? '98%' : '300px', // Full width on mobile, fixed width on desktop
        height: isMobile ? 'auto' : '375px', // Adjust height based on screen size
        cursor: 'pointer',
        position: 'relative',
        ...(event.event_user_state?.answer == null && !isEventInThePast
          ? {
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: 6, // More prominent shadow by default for actionable cards
            '&:hover': {
              boxShadow: 9, // Even more prominent on hover for actionable cards
            },
          }
          : {
            boxShadow: 3, // Default shadow for other cards
            '&:hover': {
              boxShadow: 6, // Default hover shadow for other cards
            },
          }),
      })}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0, 0, 0, 0.54)',
            color: 'white',
            padding: '10px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5">
            {t('eventCard.updatingStatus')}
          </Typography>
          <Typography variant="body1">
            {t('eventCard.pleaseWait')}
          </Typography>
        </Box>
      )}

      {event.event_user_state?.answer == null && !isEventInThePast && (
        <Badge
          variant="dot"
          color="primary" // Use primary color for the badge
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2, // Ensure badge is above the image but below other potential overlays if any
            '& .MuiBadge-dot': {
              width: 15, // Initial size
              height: 15, // Initial size
              borderRadius: '50%', // Keep it circular
              animation: 'pulse 2s infinite', // Apply animation
            },
            // Define the keyframes directly within the sx prop (supported by MUI)
            // Or define globally in a CSS file and reference here
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1,
              },
              '50%': {
                transform: 'scale(1.5)', // Slightly less than 2x for a more subtle pulse
                opacity: 0.7,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 1,
              },
            },
          }}
        />
      )}
      {isEventInThePast && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(200, 200, 200, 0.1)', // Light gray with 50% opacity
            zIndex: 1, // Ensure overlay is on top of other card content but below interactive elements if any are still active
            pointerEvents: 'none', // <-- Correction cruciale : l'overlay ne bloque plus les interactions
          }}
        />
      )}
      <Tooltip title={event.description || event.show?.description || ''} arrow>
        {/* Wrap CardMedia with a Box for positioning the Badge relative to the image area */}
        <Box sx={{ position: 'relative' }} onClick={handleCardClick}>
          <CardMedia
            component="img"
            height="140"
            image={event.images || 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y29uY2VydHxlbnwwfHwwfHx8MA%3D%3D'}
            alt={event.title}
            onClick={() => handleEventClick(event.event_id)} // Make media clickable
          />
        </Box>
      </Tooltip>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: 'left',
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', // Forcer sur une seule ligne
              width: '100%',
              display: 'block'
            }}
          >
            {formatDate(event.start_time)}
          </Typography>
        </div>
        <div>
          <Tooltip title={event.title} arrow onClick={handleCardClick}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              sx={{
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                whiteSpace: 'normal',
                maxHeight: '3.2em',
                minHeight: '3.2em', // Réserve toujours 2 lignes
                fontSize: '1.1rem',
              }}
            >
              {event.title}
            </Typography>
          </Tooltip>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
            {event.location_details.space || 'Active'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
            {event.global_user_state.is_interested_count} {event.global_user_state.is_interested_count > 1 ? t('eventCard.interesteds') : t('eventCard.interested')}, {event.global_user_state.is_participating_count} {event.global_user_state.is_participating_count > 1 ? t('eventCard.participants') : t('eventCard.participant')}
          </Typography>

          {!isEventInThePast && (
            <Grid container spacing={2}>
              <Grid size={10}>
                <Box id="event-status-buttons" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, marginTop: 2, marginBottom: 1 }}>
                  <ButtonGroup orientation="vertical" aria-label="Vertical Basic button group" sx={{ justifyContent: 'center', fontSize: '0.5rem', width: '100%', mx: 0 }}>
                    {isInterested ?
                      <Button onClick={(e) => handleStatusChange(event, 'interested')} variant="contained" sx={{ fontSize: isMobile ? '0.6rem' : '0.5rem' }} startIcon={<ThumbUpFilledIcon />}> {t('eventCard.interested')}</Button>
                      :
                      <Button onClick={(e) => handleStatusChange(event, 'interested')} sx={{ fontSize: isMobile ? '0.6rem' : '0.5rem' }} startIcon={<ThumbUpOutlinedIcon />}>{t('eventCard.interested')}</Button>
                    }

                    {isParticipating ?
                      <Button onClick={(e) => handleStatusChange(event, 'participate')} variant="contained" sx={{ fontSize: isMobile ? '0.6rem' : '0.5rem' }} startIcon={<LocalActivityIcon />}> {t('eventCard.participate')}</Button>
                      :
                      <Button onClick={(e) => handleStatusChange(event, 'participate')} sx={{ fontSize: isMobile ? '0.6rem' : '0.5rem' }} startIcon={<LocalActivityOutlinedIcon />}> {t('eventCard.participate')}</Button>
                    }
                  </ButtonGroup>
                </Box>
              </Grid>
              <Grid size={2}>
                {!isEventInThePast && event.is_need_booking && (event.event_user_state?.answer === 'participate') && (
                  <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1, marginTop: 2, marginBottom: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <AddToCalendarButton {...calendarProps}
                        label={t('eventCard.calendarButtonLabel')}
                        styleLight="--btn-underline: rgb(250, 225, 89); --btn-background: rgb(250, 225, 89); --btn-text: #111; --btn-border: none; --btn-shadow: none; --btn-font-size: 0.8rem; --btn-padding: 0.5em 1.2em; --btn-radius: 6px; --font: inherit;"
                        styleDark="--btn-underline: rgb(250, 225, 89); --btn-background: rgb(250, 225, 89); --btn-text: #111; --btn-border: none; --btn-shadow: none; --btn-font-size: 0.8rem; --btn-padding: 0.5em 1.2em; --btn-radius: 6px; --font: inherit;"
                        lightMode="bodyScheme"
                        buttonStyle='simple'
                        buttonsList={true}
                        hideIconList={true}
                        hideIconModal={true}
                        hideTextLabelButton={true}
                        hideTextLabelList={false}
                        trigger='Hover'
                        listStyle='dropdown-static'
                        className="add-to-calendar-centered"
                        size="3"
                      />
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EventCardV3;
